/* eslint-disable no-useless-escape */
import * as React from 'react';
import StandardModal from '../../../common/modals/StandardModal';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import { ToggleField, Button, Grid, Icon } from '../../../common';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractAWSRulesForm';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import SelectField from '../../../common/form-controls/SelectField';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import { AWSRuleRegion } from '../../../../models/enums/POR/AWSRuleRegion';
import { TrafficDirection } from '../../../../models/enums/POR/TrafficDirection';
import ContainerLayout from '@emorg-prd/standard-react/layouts/ContainerLayout/ContainerLayout';

interface RuleModalAccToDMZProps extends RulesFormProps {
}

interface AccToDMZRuleStateForm extends IRuleStateForm {
    trafficDirection: TrafficDirection;
}
interface RuleModalAccToDMZState {
    stateForm: AccToDMZRuleStateForm;
    availableProtocols: string[];
}

const MAX_PROTOCOLS = 4;

export default class RuleModalAccToDMZ extends AbstractRulesForm<
    RuleModalAccToDMZProps,
    RuleModalAccToDMZState
> {

    constructor(props: RuleModalAccToDMZProps) {
        super(props);
        this.state = {
            stateForm: this.props.isFromHistory ? this.getRuleCopy() : { ...this.getDefaultForm(), trafficDirection: TrafficDirection.In },
            availableProtocols: ['TCP', 'UDP', 'ICMP echo-request', 'ICMP echo-reply'],
        };

        this.initFormModel();
    }

    initFormModel() {
        super.initFormModel();

        this.addIpsRuleValidation();

        this.formModel.addField('trafficDirection', {
            getValue: () => this.state.stateForm.trafficDirection,
            validation: {
                required: this.hasRuleDirection()
            }
        })
    }

    statePropWasUpdated(firstProp: any, secondProp: any) {
        return firstProp != secondProp;
    }

    componentDidUpdate(prevProps: any): void {
        super.componentDidUpdate(prevProps)

        if (this.statePropWasUpdated(prevProps.porDirection.name, this.props.porDirection.name)) {
            this.addIpsRuleValidation();
        }
    }

    accountToAccountIpsValidation() {
        this.formModel.fields.sourceIPs.validation.rules.push(
            this.commaSeparatorValidation(),
            this.IpIsInAddressSpacesRule("primaryAccountVirtualPrivateClouds"),
            this.IpInputValidationRule({
                onlyPrivateAddresses: false
            })
        );

        this.formModel.fields.destinationIPs.validation.rules.push(
            this.commaSeparatorValidation(),
            this.IpIsInAddressSpacesRule("secondaryAccountVirtualPrivateClouds"),
            this.IpInputValidationRule({
                onlyPrivateAddresses: false
            })
        );
    }

    ruleIpValidationDependingOnTrafficDirection = (trafficDirecionKey: string) => {
        if (!trafficDirecionKey) return;
        const validations = {
            In: () => {
                this.sourceIpsValidation()
            },
            Out: () => {
                this.destinationIpsValidation()
            }
        }

        validations[trafficDirecionKey]()
    }

    addIpsRuleValidation() {
        this.refreshIpsRuleValidation();

        if (this.props.isReadOnly) {
            this.removeIpsRuleValidation()
            return;
        }

        const validations = {
            GLLOnPremToGLLAWS: () => {
                this.ruleIpValidationDependingOnTrafficDirection(this.state.stateForm.trafficDirection?.name);
            },
            GLLAWSToGLLAWS: () => {
                this.accountToAccountIpsValidation();
            },
            GLLAWSToGLLUIZL4: () => {
                this.ruleIpValidationDependingOnTrafficDirection(this.state.stateForm.trafficDirection?.name)
            },
            UntrustedToL4: () => {
                this.ruleIpValidationDependingOnTrafficDirection(this.state.stateForm.trafficDirection?.name)
            },
            UntrustedToInternet: () => {
                this.sourceIpsValidation();
            },
            ExternalAccounts: () => {
                this.UntrustedIpsValidation();
            }
        }

        this.formModel.fields.destinationIPs.validation.rules.push(this.numberOfDestinationItemsValidation());
        this.formModel.fields.destinationURLs.validation.rules.push(this.banHashCharacterValidation());

        validations[this.props.porDirection.name]();
    }

    // eslint-disable-next-line class-methods-use-this
    getDefaultFormState() {
        return {
            isTemporary: false,
            expirationDate: null,
            operation: RuleOperation.Add,
            name: '',
            destinationURLs: '',
            destinationPorts: [{ protocol: '', destinationPorts: '' }],
        };
    }

    availableRegions = () => {
        return [
            AWSRuleRegion.Ohio,
            AWSRuleRegion.Singapure,
            AWSRuleRegion.London
        ]
    }

    getRegionOptions(): AWSRuleRegion[] {
        var accToInternetRegions: AWSRuleRegion[] = []

        if (!this.itsExternalAccountsDirection())
            accToInternetRegions = new AWSRuleRegion().awsListByDirection(PortOpeningRequestsDirection.AccountToInternet, this.props.virtualPrivateClouds.primaryAccountVirtualPrivateClouds);
        else accToInternetRegions = this.availableRegions()

        return accToInternetRegions;
    }

    addProtocol = () => {
        if (this.state.stateForm.destinationPorts.length < MAX_PROTOCOLS) {
            const updatedProtocols = [...this.state.stateForm.destinationPorts];
            updatedProtocols.push({ protocol: '', destinationPorts: '' });
            this.formModel.fields.destinationPorts.validation.rules.push(
                {
                    assert: () => this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.protocol !== '',
                    message: 'Empty values are not allowed.'
                },
                {
                    assert: () => !this.hasEmptyValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.destinationPorts),
                    message: 'Empty values are not allowed.'
                },
                {
                    assert: () => {
                        const ports = this.parseMultipleValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.destinationPorts)?.split(';');
                        return ports.every(port => this.isPortInputValid(port, 0));
                    },
                    message: () => {
                        const invalidPort = this.parseMultipleValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length - 1]?.destinationPorts).split(';').find(port => !this.isPortInputValid(port, 0));
                        return `${invalidPort} is an invalid port input.`;
                    }
                }
            );
            this.handleStateFormChange('destinationPorts', updatedProtocols);
        }
    };

    handleProtocolChange = (index: number, field: string, value: string) => {
        const updatedProtocols = [...this.state.stateForm.destinationPorts];
        updatedProtocols[index][field] = value;
        this.handleStateFormChange('destinationPorts', updatedProtocols);
    };

    removeProtocol = (index: number) => {
        const updatedProtocols = this.state.stateForm.destinationPorts.filter((_, i) => i !== index);
        this.handleStateFormChange('destinationPorts', updatedProtocols);
        this.formModel.fields.destinationPorts.validation.rules = this.formModel.fields.destinationPorts.validation.rules?.filter((_, i) => i !== index);
    };

    handleTrafficDirectionChange = (event: any) => {
        const direction = new TrafficDirection().fromValue(event.target.value)

        this.handleStateFormChange('trafficDirection', direction);

        this.refreshIpsRuleValidation();

        this.ruleIpValidationDependingOnTrafficDirection(direction.name)
    }

    // Rendering protocols dynamically
    renderProtocols() {
        return this.state.stateForm.destinationPorts?.map((protocol, index) => {

            const selectedProtocols = this.state.stateForm.destinationPorts
                .slice(0, index)
                .map(p => p.protocol);
            const availableOptions = this.state.availableProtocols.filter(
                protocol => !selectedProtocols.includes(protocol)
            );

            return (
                <Grid variant="halves" key={index} className="protocol-row-grid">
                    <Grid.Item>
                        <TextAreaField
                            id={`destinationPorts-${index}`}
                            note='Multiple values are allowed, separated by semicolons. Port ranges are accepted.'
                            value={this.state.stateForm.destinationPorts[index]?.destinationPorts}
                            validation={this.formModel.fields.destinationPorts.validation}
                            label={`Destination Port(s) for Protocol ${index + 1}`}
                            onChange={(e) => this.handleProtocolChange(index, 'destinationPorts', e.target.value)}
                            disabled={!this.formCanBeEdited()}
                        />
                    </Grid.Item>
                    <Grid.Item style={{ alignSelf: 'baseline' }}>
                        <SelectField
                            label={`Protocol ${index + 1}`}
                            id={`protocol-${index}`}
                            value={this.state.stateForm.destinationPorts[index]?.protocol}
                            validation={this.formModel.fields.destinationPorts.validation}
                            options={availableOptions}
                            onChange={(e) => this.handleProtocolChange(index, 'protocol', e.target.value)}
                            disabled={!this.formCanBeEdited()}
                        />
                    </Grid.Item>
                    <Grid.Item style={{ width: '10%' }}>
                        {index != 0 &&
                            <Button
                                style={{ zIndex: 0 }}
                                variant="primary"
                                size="small"
                                onClick={() => this.removeProtocol(index)}
                                disabled={!this.formCanBeEdited()}
                            >
                                <Icon icon="trashcan" size="small" />
                            </Button>
                        }
                    </Grid.Item>
                </Grid>
            )
        });
    }

    wasEditted() {
        if (this.props.editingRule) {
            return (
                this.props.editingRule.description != this.stateFormHandler().description.value
                || this.props.editingRule.action.value != this.stateFormHandler().action.value
                || this.props.editingRule.sourceIPs != this.stateFormHandler().sourceIPs.value
                || this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value
                || this.props.editingRule.destinationURLs != this.state.stateForm.destinationURLs
                || this.props.editingRule.destinationPorts != this.state.stateForm.destinationPorts
                || this.props.editingRule.sendDND != this.state.stateForm.sendDND
                || (this.props.editingRule.operation.value == RuleOperation.Add.value && (this.props.editingRule.name != this.state.stateForm.name))
            )
        }
        return true
    }

    hasRuleDirection = () => {
        return this.props.porDirection != undefined &&
            (this.props.porDirection.name == PortOpeningRequestsDirection.GLLOnPremToGLLAWS.name ||
                this.props.porDirection.name == PortOpeningRequestsDirection.GLLAWSToGLUizL4.name ||
                this.props.porDirection.name == PortOpeningRequestsDirection.UntrustedToL4.name)
    }

    trafficDirections = () => {
        const trafficDirections = {
            GLLOnPremToGLLAWS: [
                { text: 'Inbound (GLLAWS -> GLLOnPrem)', value: TrafficDirection.In.value },
                { text: 'Outbound (GLLOnPrem -> GLLAWS)', value: TrafficDirection.Out.value },
            ],
            GLLAWSToGLLUIZL4: [
                { text: 'Inbound (GLLAWS -> GLLUizL4)', value: TrafficDirection.In.value },
                { text: 'Outbound (GLLUizL4 -> GLLAWS)', value: TrafficDirection.Out.value },
            ],
            UntrustedToL4: [
                { text: 'Inbound (Untrusted -> L4)', value: TrafficDirection.In.value },
                { text: 'Outbound (L4 -> Untrusted)', value: TrafficDirection.Out.value },
            ],
        }

        return this.props.porDirection == undefined ? [] : trafficDirections[this.props.porDirection.name];
    }

    itsExternalAccountsDirection = () => {
        return this.props.porDirection != undefined && PortOpeningRequestsDirection.ExternalAccounts.name
    }

    render() {
        return (
            <StandardModal
                optionalClass="ruleModalAWS"
                show={this.props.visible}
                title={this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
                onDismiss={this.props.onClose as () => any}
                footer={
                    !this.props.isReadOnly &&
                    <Grid variant="2up">
                        <Grid.Item>
                            <Button
                                variant="primary"
                                disabled={(!this.formModel.isValid() || !this.wasEditted()) && this.state.stateForm.operation.name !== RuleOperation.Remove.name}
                                onClick={this.handleRequest}
                            >
                                {this.props.editingRule ? 'Confirm' : 'Create'}
                            </Button>
                        </Grid.Item>
                        <Grid.Item>
                            <Button
                                variant="secondary"
                                onClick={this.handleCancel}
                            >
                                Cancel
                            </Button>
                        </Grid.Item>
                    </Grid>
                }
            >
                {!this.isModalForNewRule() &&
                    <ToggleField
                        id=''
                        {...this.stateFormHandler().operation}
                        label="Operation"
                        disabled={this.props.isReadOnly}
                        value={this.state.stateForm.operation?.value}
                        options={this.props.isReadOnly ?
                            [
                                {
                                    value: new RuleOperation().fromValue(this.stateFormHandler().operation.value).value,
                                    text: new RuleOperation().fromValue(this.stateFormHandler().operation.value).name,
                                }
                            ] :
                            [
                                { value: RuleOperation.Modify.value, text: RuleOperation.Modify.name },
                                { value: RuleOperation.Remove.value, text: RuleOperation.Remove.name }
                            ]
                        }
                    />
                }
                <Grid variant="2up">
                    <Grid.Item>
                        <TextInputField
                            label="Rule Name"
                            value={this.state.stateForm.name}
                            validation={this.formModel.fields.name.validation}
                            onChange={
                                (event) => {
                                    this.handleStateFormChange('name', event.target.value);
                                }
                            }
                            disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
                        />
                    </Grid.Item>
                    <Grid.Item>
                        <SelectField
                            {...this.stateFormHandler().region}
                            options={this.getRegionOptions()}
                            disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
                        />
                    </Grid.Item>
                </Grid>
                {this.state.stateForm.operation?.name !== RuleOperation.Remove.name &&
                    <>
                        <Grid variant="2up">
                            <Grid.Item>
                                <ToggleField
                                    id='action-toggle'
                                    {...this.stateFormHandler().action}
                                    options={(this.formCanBeEdited()) ? [
                                        { value: RuleAction.Allow.value, text: RuleAction.Allow.name },
                                        { value: RuleAction.Block.value, text: RuleAction.Block.name },
                                    ] : this.stateFormHandler().action?.value ?
                                        [{ value: RuleAction.Allow.value, text: RuleAction.Allow.name }]
                                        :
                                        [{ value: RuleAction.Block.value, text: RuleAction.Block.name }]
                                    }
                                    label="Action"
                                    disabled={!this.formCanBeEdited()}
                                />
                            </Grid.Item>
                            {this.hasRuleDirection() ?
                                <Grid.Item>
                                    <SelectField
                                        label="Traffic Direction"
                                        value={this.state.stateForm.trafficDirection?.value}
                                        options={this.trafficDirections()}
                                        className="full-width"
                                        onChange={(event) => { this.handleTrafficDirectionChange(event) }}
                                        validation={this.formModel.fields.trafficDirection.validation}
                                        disabled={!this.formCanBeEdited()}
                                    />
                                </Grid.Item>
                                : <></>}
                        </Grid>
                        <Grid variant="2up">
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().sourceIPs}
                                    label="Source (IPs / IP Range / CIDR)"
                                    disabled={!this.formCanBeEdited()}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().destinationIPs}
                                    label="Destination (IPs / IP range / CIDR)"
                                    disabled={!this.formCanBeEdited() || (this.state.stateForm.destinationURLs?.trim().length > 0)}
                                />
                            </Grid.Item>
                        </Grid>
                        <Grid variant="2up">
                            <Grid.Item>
                                <TextAreaField
                                    label="Destination (URLs)"
                                    value={this.state.stateForm.destinationURLs}
                                    validation={this.formModel.fields.destinationURLs.validation}
                                    onChange={
                                        (event) => {
                                            this.handleStateFormChange('destinationURLs', event.target.value);
                                        }
                                    }
                                    note='Multiple values are allowed, separated by semicolons.'
                                    disabled={!this.formCanBeEdited() || (this.state.stateForm.destinationIPs?.trim().length > 0)}
                                />
                            </Grid.Item>
                        </Grid>
                        <ContainerLayout optionalClass="ruleModal-Container" type="em-l-container">
                            {this.renderProtocols()}
                            <Grid>
                                <Grid.Item />
                                <Grid.Item>
                                    <Button
                                        variant="primary"
                                        size="small"
                                        style={{ width: '30%', alignSelf: 'flex-end' }}
                                        onClick={() => this.addProtocol()}
                                        disabled={!this.formCanBeEdited() || this.state.stateForm?.destinationPorts.length >= MAX_PROTOCOLS}
                                    >
                                        <>
                                            <Icon icon="plus-filled" size="small" />
                                            <span>Add Protocol</span>
                                        </>
                                    </Button>
                                </Grid.Item>
                            </Grid>
                        </ContainerLayout>
                        <header className="em-c-section__header" />
                        <Grid variant="2up">
                            <Grid.Item>
                                <TextAreaField
                                    {...this.stateFormHandler().description}
                                    label="Description"
                                    disabled={!this.formCanBeEdited()}
                                />
                            </Grid.Item>
                            <Grid.Item />
                        </Grid>
                    </>}
            </StandardModal>
        );
    }
}